import { type AnyFieldMetaBase } from '@tanstack/react-form';
import { ComponentPropsWithRef } from 'react';

import FormLabel from '~/common/components/Forms/FormLabel';
import Text from '~/common/components/Texts/Text';
import { useFieldContext } from '~/common/form/formContexts';
import { DROPDOWN_LABEL } from '~/pages/ClassifyDocumentsV2/constants';

import {
  StyledXMarkIcon as CloseIcon,
  StyledAutocomplete,
  StyledChip,
  StyledOption,
  StyledTextField,
} from './StyledSelectElements';

const defaultPlaceHolder = (isMultiple: boolean, placeholder: string) => {
  if (placeholder && placeholder !== '') {
    return placeholder;
  }
  return isMultiple ? 'Select one or more' : 'Select';
};

type MultiSelectProps = ComponentPropsWithRef<typeof StyledAutocomplete> & {
  label: string;
  isMultiple?: boolean;
  isLabelHidden?: boolean;
  isLabelMultiple?: boolean;
  id?: string;
  width?: string;
  value?: any[];
  options: any[];
  errors?: boolean;
  helperText?: string;
  clearErrors?: (...args: any[]) => any;
  placeholder?: string;
  useTanstackForm?: boolean;
};

function buildFieldErrors(
  fieldId: string | undefined,
  errorMessages: unknown[],
): Record<string, { message: string }> {
  if (!fieldId || errorMessages.length === 0) return {};
  return { [fieldId]: { message: errorMessages[0] as string } };
}

function TanstackFormMultiSelect({
  id,
  ...props
}: Readonly<
  Omit<MultiSelectProps, 'useTanstackForm' | 'errors' | 'clearErrors' | 'value'>
>) {
  const field = useFieldContext<unknown>();
  const errors = buildFieldErrors(id, field.state.meta.errors);
  const clearErrors = () =>
    field.setMeta((prev: AnyFieldMetaBase) => ({ ...prev, errorMap: {} }));

  return (
    <BaseMultiSelect
      {...props}
      id={id}
      value={field.state.value as any}
      onChange={(_event: unknown, option: unknown) => {
        field.handleChange(option);
      }}
      errors={errors as any}
      clearErrors={clearErrors}
    />
  );
}

export default function MultiSelect({
  useTanstackForm = false,
  ...props
}: Readonly<MultiSelectProps>) {
  if (useTanstackForm) {
    return <TanstackFormMultiSelect {...props} />;
  }
  return <BaseMultiSelect {...props} />;
}

function BaseMultiSelect({
  value,
  label,
  isLabelHidden = false,
  isLabelMultiple = false,
  isMultiple = false,
  id,
  options,
  errors,
  helperText,
  clearErrors,
  placeholder = '',
  width = '100%',
  ...props
}: Readonly<MultiSelectProps>) {
  const isYAML = errors === true || errors === false;

  return (
    <>
      {!isLabelHidden && (
        <FormLabel htmlFor={id}>
          <Text fontSize="sm">
            {isLabelMultiple
              ? `Select ${DROPDOWN_LABEL[label]} Record${
                  isMultiple ? '(s)' : ''
                }`
              : label}
          </Text>
        </FormLabel>
      )}
      <StyledAutocomplete
        id={id}
        width={width}
        multiple={isMultiple}
        options={options}
        value={value || (isMultiple ? [] : null)} // Ensure value is an array or null for multiple select
        renderTags={(value, getTagProps) => {
          return value.map((option, index) => {
            if (!option) return null;
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <StyledChip
                variant="outlined"
                label={option.label}
                deleteIcon={<CloseIcon />}
                key={key}
                {...tagProps}
              />
            );
          });
        }}
        renderOption={(props, option) => (
          <StyledOption {...props}>{option.label}</StyledOption>
        )}
        renderInput={(params) => {
          return (
            <StyledTextField
              {...params}
              inputProps={{
                ...params.inputProps,
                ...(isLabelHidden ? { 'aria-label': label } : {}),
              }}
              error={
                typeof errors === 'object' && errors !== null
                  ? !!errors[id]
                  : !!errors
              }
              helperText={isYAML && errors ? helperText : errors[id]?.message}
              placeholder={
                value &&
                (typeof value === 'string' ||
                  (Array.isArray(value) && value.length > 0))
                  ? ''
                  : defaultPlaceHolder(isMultiple, placeholder)
              }
              onBlur={() => {
                if (value && typeof clearErrors === 'function') {
                  clearErrors([id]);
                }
              }}
            />
          );
        }}
        {...props}
      />
    </>
  );
}
